import React, { useEffect, useState } from 'react';
import SideDrawer from './SideDrawer';
import SupplierProfileTab from './SupplierProfileTab';
import SupplierHelpContactTab from './SupplierHelpContactTab';
import StatusHexagon from './portal/StatusHexagon';

// Supplier-portal slide-out – two tabs (Profile / Help & Contact)
// sharing one drawer shell. Driven by SideDrawer for focus trap,
// escape-to-close, click-outside-to-close, body-scroll-lock.
//
// Props:
//   open         – boolean
//   onClose      – close handler (escape, backdrop, X button)
//   initialTab   – 'profile' | 'help' – which tab to land on; resets
//                  to that tab whenever `open` flips from false → true
//                  so re-opening from a different trigger always
//                  starts in the right place. Mid-session tab toggles
//                  by the user are preserved within the open lifecycle.
//   supplier     – full row from `suppliers` (selected with `*`)
//
// The slide-out doesn't own URL state – both triggers (account
// dropdown and utility bar nav) explicitly pass an initialTab, and
// help-article links navigate away rather than deep-linking the
// drawer itself. Keeps refresh behaviour predictable (page reload
// always lands on the supplier portal home, no orphan drawer state).

const TabButton = ({ active, icon, label, id, controls, onClick, onKeyDown, tabRef }) => (
  <button
    ref={tabRef}
    type="button"
    role="tab"
    id={id}
    aria-selected={active}
    aria-controls={controls}
    tabIndex={active ? 0 : -1}
    onClick={onClick}
    onKeyDown={onKeyDown}
    className={`flex items-center gap-2 px-4 py-3.5 text-[11px] font-bold tracking-[0.2em] uppercase border-b-2 -mb-px bg-transparent border-l-0 border-r-0 border-t-0 cursor-pointer font-['Metropolis'] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159]/50 rounded-t ${
      active
        ? 'text-fusion-forest border-b-[#92D400]'
        : 'text-[#51534A] border-b-transparent hover:text-ink'
    }`}>
    <span aria-hidden="true">{icon}</span>
    {label}
  </button>
);

// Tab icons are hexagons (account §2 – drawer tabs are on the hexagon tier),
// teal informational fill. The active tab reads via the underline + label
// colour, so both tabs keep the same teal mark.
const profileIcon = (
  <StatusHexagon glyph={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>} fg="#2E8FA1" size={22} />
);
const helpIcon = (
  <StatusHexagon glyph={<><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} fg="#2E8FA1" size={22} />
);

export default function SupplierSlideout({ open, onClose, initialTab = 'profile', supplier, supplierUser }) {
  // Tab state is local so the user can flip between Profile and Help
  // mid-session without losing scroll. On open we sync to whichever
  // initialTab the trigger asked for – that's the source of truth at
  // launch time, not whatever tab was last visible.
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Roving arrow-key navigation across the tablist (§7). Left/Right (and
  // Home/End) move between tabs and move focus with the selection.
  const profileTabRef = React.useRef(null);
  const helpTabRef = React.useRef(null);
  const onTabKeyDown = (e) => {
    const order = ['profile', 'help'];
    const refs = { profile: profileTabRef, help: helpTabRef };
    let next;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = order[(order.indexOf(tab) + 1) % order.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = order[(order.indexOf(tab) + order.length - 1) % order.length];
    else if (e.key === 'Home') next = order[0];
    else if (e.key === 'End') next = order[order.length - 1];
    else return;
    e.preventDefault();
    setTab(next);
    refs[next].current?.focus();
  };

  return (
    <SideDrawer open={open} onClose={onClose} width={480} ariaLabel="Your details and help">

      {/* Header – eyebrow + title only. The brand-stripe (3px green
          gradient) is rendered by SideDrawer itself. */}
      <div className="px-6 pt-4 pb-3 bg-[#92D400]/[0.04] border-b border-line flex items-start justify-between gap-4 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <div className="font-['Metropolis'] text-[10px] font-extrabold tracking-[0.25em] uppercase text-ink-secondary mb-1">
            Account
          </div>
          <div className="font-['Metropolis'] text-lg font-extrabold text-ink">
            Your details
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="w-8 h-8 flex items-center justify-center bg-transparent border border-line rounded text-[#51534A] hover:text-ink hover:border-[#DADCCD] transition-colors flex-shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Tab bar */}
      <div role="tablist" aria-label="Your details and help" className="px-6 flex gap-0 bg-white border-b border-line flex-shrink-0">
        <TabButton active={tab === 'profile'} tabRef={profileTabRef} id="tab-profile" controls="panel-profile" icon={profileIcon} label="Profile" onClick={() => setTab('profile')} onKeyDown={onTabKeyDown} />
        <TabButton active={tab === 'help'} tabRef={helpTabRef} id="tab-help" controls="panel-help" icon={helpIcon} label="Help & Contact" onClick={() => setTab('help')} onKeyDown={onTabKeyDown} />
      </div>

      {/* Body – scrollable. Renders the active tab as its labelled panel. */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {tab === 'profile' && (
          <div role="tabpanel" id="panel-profile" aria-labelledby="tab-profile" tabIndex={0} className="focus:outline-none">
            <SupplierProfileTab supplier={supplier} user={supplierUser} onSwitchToHelpTab={() => setTab('help')} />
          </div>
        )}
        {tab === 'help' && (
          <div role="tabpanel" id="panel-help" aria-labelledby="tab-help" tabIndex={0} className="focus:outline-none">
            <SupplierHelpContactTab onClose={onClose} />
          </div>
        )}
      </div>
    </SideDrawer>
  );
}
